import {isNil} from 'ramda';

import {Base} from '../base/Base';
import type {ViewPartVariants} from '../CarDiagram.types';
import {FrontBumper} from '../parts/exterior/FrontBumper';
import {FrontLeftHeadlight} from '../parts/exterior/FrontLeftHeadlight';
import {FrontLeftSidePanel} from '../parts/exterior/FrontLeftSidePanel';
import {FrontRightHeadlight} from '../parts/exterior/FrontRightHeadlight';
import {FrontRightSidePanel} from '../parts/exterior/FrontRightSidePanel';
import {Grille} from '../parts/exterior/Grille';
import {Hood} from '../parts/exterior/Hood';
import {RearBumper} from '../parts/exterior/RearBumper';
import {RearLeftTaillight} from '../parts/exterior/RearLeftTaillight';
import {RearRightTaillight} from '../parts/exterior/RearRightTaillight';
import {TowBar} from '../parts/exterior/TowBar';
import {CenterConsole} from '../parts/interior/CenterConsole';
import {DashboardBottom} from '../parts/interior/DashboardBottom';
import {DashboardMiddleHeating} from '../parts/interior/DashboardMiddleHeating';
import {DashboardTop} from '../parts/interior/DashboardTop';
import {DriverSeat} from '../parts/interior/DriverSeat';
import {Floor} from '../parts/interior/Floor';
import {FrontLeftDoorInterior} from '../parts/interior/FrontLeftDoor';
import {FrontRightDoorInterior} from '../parts/interior/FrontRightDoor';
import {LeftMiddlePillar} from '../parts/interior/LeftMiddlePillar';
import {PassengerSeat} from '../parts/interior/PassengerSeat';
import {RearBoard} from '../parts/interior/RearBoard';
import {RearLeftDoorInterior} from '../parts/interior/RearLeftDoor';
import {RearLeftSidePanelInterior} from '../parts/interior/RearLeftSidePanel';
import {RearRightDoorInterior} from '../parts/interior/RearRightDoor';
import {RearRightSidePanelInterior} from '../parts/interior/RearRightSidePanel';
import {RearSeats} from '../parts/interior/RearSeats';
import {RightMiddlePillar} from '../parts/interior/RightMiddlePillar';
import {SteeringWheel} from '../parts/interior/SteeringWheel';
import {TrunkInterior} from '../parts/interior/Trunk';

interface InteriorViewProps {
  readonly partVariants: ViewPartVariants;
  readonly onPartClick?: (partId: string) => void;
}

export function InteriorView(props: InteriorViewProps) {
  const onPartClick = props.onPartClick;
  const clickable = (partId: string): (() => void) | undefined => {
    if (isNil(onPartClick)) {
      return undefined;
    }
    return () => onPartClick(partId);
  };

  return (
    <div className="relative h-[502px] w-[1342px]">
      {/* SHARED EXTERIOR PARTS - background */}
      <Base />
      <FrontBumper />
      <FrontRightHeadlight />
      <FrontLeftHeadlight />
      <FrontRightSidePanel />
      <FrontLeftSidePanel />
      <Grille />
      <RearBumper />
      <TowBar />
      <Hood />
      <RearRightTaillight />
      <RearLeftTaillight />
      {/* INTERIOR SPECIFIC PARTS */}
      <Floor />
      <DashboardBottom />
      <DashboardMiddleHeating
        variant={props.partVariants['dashboard-middle-heating'] ?? 'default'}
        onClick={clickable('dashboard-middle-heating')}
      />
      <SteeringWheel
        variant={props.partVariants['steering-wheel'] ?? 'default'}
        onClick={clickable('steering-wheel')}
      />
      <DashboardTop />
      <FrontLeftDoorInterior
        variant={props.partVariants['front-left-door-interior'] ?? 'default'}
        onClick={clickable('front-left-door-interior')}
      />
      <FrontRightDoorInterior
        variant={props.partVariants['front-right-door-interior'] ?? 'default'}
        onClick={clickable('front-right-door-interior')}
      />
      <RearLeftDoorInterior
        variant={props.partVariants['rear-left-door-interior'] ?? 'default'}
        onClick={clickable('rear-left-door-interior')}
      />
      <RearRightDoorInterior
        variant={props.partVariants['rear-right-door-interior'] ?? 'default'}
        onClick={clickable('rear-right-door-interior')}
      />
      <LeftMiddlePillar />
      <RightMiddlePillar />
      <RearLeftSidePanelInterior
        variant={props.partVariants['rear-left-side-panel-interior'] ?? 'default'}
        onClick={clickable('rear-left-side-panel-interior')}
      />
      <RearRightSidePanelInterior
        variant={props.partVariants['rear-right-side-panel-interior'] ?? 'default'}
        onClick={clickable('rear-right-side-panel-interior')}
      />
      <TrunkInterior
        variant={props.partVariants['trunk-interior'] ?? 'default'}
        onClick={clickable('trunk-interior')}
      />
      <RearBoard />
      <CenterConsole
        variant={props.partVariants['center-console'] ?? 'default'}
        onClick={clickable('center-console')}
      />
      <DriverSeat
        variant={props.partVariants['driver-seat'] ?? 'default'}
        onClick={clickable('driver-seat')}
      />
      <PassengerSeat
        variant={props.partVariants['passenger-seat'] ?? 'default'}
        onClick={clickable('passenger-seat')}
      />
      <RearSeats
        variant={props.partVariants['rear-seats'] ?? 'default'}
        onClick={clickable('rear-seats')}
      />
    </div>
  );
}
